export function safeParseJson<T>(value: string, fallback: T): T {
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

export const getParsedPlateEditorValue = (content?: string | null) => {
  return content
    ? safeParseJson(content, [])
    : [
        {
          type: 'p',
          children: [
            {
              text: '',
            },
          ],
        },
      ];
};

export type ParsedPlateEditorValue = ReturnType<typeof getParsedPlateEditorValue>;

type PlateTextNode = {
  text?: unknown;
};

type PlateBlockNode = {
  children?: PlateTextNode[];
};

export const hasPlateTextContent = (parsedContent: unknown): boolean => {
  if (!Array.isArray(parsedContent) || parsedContent.length === 0) {
    return false;
  }

  return parsedContent.some((block: PlateBlockNode) => Array.isArray(block?.children)
    && block.children.some(child => typeof child?.text === 'string' && child.text.trim().length > 0));
};

export const getPlateContentState = (content?: string | null) => {
  const value = getParsedPlateEditorValue(content);

  return {
    value,
    hasTextContent: hasPlateTextContent(value),
  };
};

export const pad = (n: number) => String(n).padStart(2, '0');

/**
 * Workaround for a Slack iOS bug: links opened from Slack via `navigator.share()`
 * get their query string HTML-entity-encoded, turning `&key=` into `&amp;key=`.
 * That leaves the query key literally as `amp;key` instead of `key`, so normal
 * query param readers (e.g. nuqs) can't find it. Use this to read the mangled
 * value straight from the URL as a fallback.
 */
export const getSlackAmpQueryParam = (key: string): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  return new URLSearchParams(window.location.search).get(`amp;${key}`);
};

export const getSlackAmpQueryParamAsFloat = (key: string): number | null => {
  const raw = getSlackAmpQueryParam(key);
  if (raw === null) {
    return null;
  }

  const parsed = Number.parseFloat(raw);
  return Number.isNaN(parsed) ? null : parsed;
};

export const objectToQueryParams = (object: Record<string, any>): string => {
  const params = new URLSearchParams();

  Object.entries(object).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    const val = typeof value === 'string' ? value : JSON.stringify(value);
    params.append(key, val);
  });

  return params.toString();
};
